import type { Database } from '../../design/services';
import type { Entity, Model, Project } from '../../design/types';

// Root project needs a stable ID because App.tsx uses it to create the database before bootstrap runs
export const rootProject = {
  id: 'mksys', name: 'mksys', url: 'localhost', color: '#000000', logo: '', design: 'shadcn', tier: 'startup', database: 'firebase', parent: 'mksys',
} as unknown as Project;

// Collection descriptor for storing model definitions (not itself a stored record)
export const MODELS = { name: 'Models', type: 'collection' as const, path: '/models', properties: [] as string[], actions: [] };

// Collection descriptor for storing action definitions (not itself a stored record)
export const ACTIONS = { name: 'Actions', type: 'collection' as const, path: '/actions', properties: [] as string[], actions: [] };

// ══════════════════════════════════════════════════════════════════════════════
// BOOTSTRAP
// ══════════════════════════════════════════════════════════════════════════════

export interface RootModels {
  propertyModel: Entity;
  actionsModel: Entity;
  userModel: Entity;
  groupModel: Entity;
  projectModel: Entity;
  capabilityModel: Entity;
  authorizationModel: Entity;
  configModel: Entity;
}

/** Seeds root meta DB with system models, groups, capabilities, authorizations, and user entities */
export async function bootstrapRoot(metaDb: Database): Promise<{ users: Entity[], models: RootModels, orderingProjectId: string }> {
  const entityDb = metaDb;
  const M = MODELS as unknown as Model;

  // ── Phase 1a: Create models (no cross-references) ──

  const [propertyModel, actionsModel, homeModel, modelModel, userModel, groupModel, projectModel, capabilityModel, authorizationModel, messageModel, notificationsModel, configModel] = await Promise.all([
    metaDb.create(M, { name: 'Properties', singular: 'property', type: 'collection', path: '/properties', icon: 'Braces', label: 'return this.name', actions: [], hidden: true }),
    metaDb.create(M, { name: 'Actions', singular: 'action', type: 'collection', path: '/actions', icon: 'Play', label: 'return this.name', actions: [], hidden: true }),
    metaDb.create(M, { name: 'Home', singular: 'Home', type: 'singleton', path: '/', icon: 'Home', actions: [] }),
    metaDb.create(M, { name: 'Models', singular: 'model', type: 'collection', path: '/models', icon: 'GitBranch', label: 'return this.name', actions: [] }),
    metaDb.create(M, { name: 'Users', singular: 'user', type: 'collection', path: '/users', icon: 'User', label: 'return this.namemail', view: 'card', actions: [] }),
    metaDb.create(M, { name: 'Groups', singular: 'group', type: 'collection', path: '/groups', icon: 'Users', label: 'return this.name', actions: [] }),
    metaDb.create(M, { name: 'Projects', singular: 'project', type: 'collection', path: '/projects', icon: 'Rocket', label: 'return this.name', actions: [] }),
    metaDb.create(M, { name: 'Capabilities', singular: 'capability', type: 'collection', path: '/capabilities', icon: 'Key', label: 'return this.action + " on " + this.model?.name', actions: [] }),
    metaDb.create(M, { name: 'Authorizations', singular: 'authorization', type: 'collection', path: '/authorizations', icon: 'ShieldCheck', label: 'return this.group?.name + " can " + this.capability?.action + " " + this.capability?.model?.name + " in " + this.project?.name', actions: [] }),
    metaDb.create(M, { name: 'Messages', singular: 'message', type: 'collection', path: '/messages', icon: 'Mail', label: 'return this.title', actions: [], hidden: true }),
    metaDb.create(M, { name: 'Notifications', singular: 'notification', type: 'collection', path: '/notifications', icon: 'Bell', actions: [] }),
    metaDb.create(M, { name: 'Configs', singular: 'config', type: 'collection', path: '/configs', icon: 'Settings', actions: [], hidden: true }),
  ]);

  // ── Phase 1b: Create models with references to other models ──

  const [myProfileModel, myNotificationsModel] = await Promise.all([
    metaDb.create(M, { name: 'My Profile', singular: 'profile', type: 'personal', path: '/profile', icon: 'CircleUser', label: "return this.name + \"'s profile\"", reference: userModel.id, actions: [] }),
    metaDb.create(M, { name: 'My Notifications', singular: 'notification', type: 'personal', path: '/my-notifications', icon: 'Bell', reference: notificationsModel.id, actions: [] }),
  ]);

  const allModels = [homeModel, propertyModel, actionsModel, modelModel, userModel, groupModel, projectModel, capabilityModel, authorizationModel, myProfileModel, messageModel, notificationsModel, myNotificationsModel, configModel];

  // ── Phase 2: Create properties ──

  const P = propertyModel as unknown as Model;

  // Property meta-properties
  const [propName, propType, propDescription, propSection, propArray, propReference, propEnum, _propOptions, propCode, propMultiline, propComputed, propReadonly, propDisabled, propHidden, propRequired, propFilter, _propDefault] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string', required: true }),
    metaDb.create(P, { name: 'type', type: 'string', enum: 'return ["string","integer","object","date","month","year","file","password","boolean","date-time","color","time","reference","image","rich-text","icon","code","geopoint","pdf","custom"]', required: true }),
    metaDb.create(P, { name: 'description', type: 'string', multiline: 3 }),
    metaDb.create(P, { name: 'section', type: 'string' }),
    metaDb.create(P, { name: 'array', type: 'boolean' }),
    metaDb.create(P, { name: 'reference', type: 'reference', reference: modelModel.id, description: 'Referenced model (for reference type)' }),
    metaDb.create(P, { name: 'enum', type: 'code', description: 'Code returning string[] for Select options, or filtering reference entities via list()' }),
    metaDb.create(P, { name: 'options', type: 'string' }),
    metaDb.create(P, { name: 'code', type: 'code', description: 'Render function for custom property type' }),
    metaDb.create(P, { name: 'multiline', type: 'integer', description: 'Number of lines for multiline text' }),
    metaDb.create(P, { name: 'computed', type: 'string', description: 'Computed value (makes field readonly)' }),
    metaDb.create(P, { name: 'readonly', type: 'string' }),
    metaDb.create(P, { name: 'disabled', type: 'string' }),
    metaDb.create(P, { name: 'hidden', type: 'string' }),
    metaDb.create(P, { name: 'required', type: 'string' }),
    metaDb.create(P, { name: 'filter', type: 'code', description: 'For reference type: returns filter object for database.list()' }),
    metaDb.create(P, { name: 'default', type: 'string' }),
  ]);

  const propEditFieldIds = [propName.id, propType.id, propDescription.id, propSection.id, propArray.id, /* propProperties added below */ propReference.id, propEnum.id, propMultiline.id, propComputed.id, propCode.id, propReadonly.id, propDisabled.id, propHidden.id, propRequired.id, propFilter.id];

  // Self-referential property (needs propertyModel.id + other prop IDs)
  const propProperties = await metaDb.create(P, { name: 'properties', type: 'reference', array: true, reference: propertyModel.id, properties: propEditFieldIds, description: 'Nested properties (for object type)' });
  propEditFieldIds.splice(5, 0, propProperties.id); // insert after propArray

  // Model meta-properties
  const [modelName, modelSingular, modelType, modelPath, modelReference, modelIcon, modelLabel, modelHidden, modelView] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string' }),
    metaDb.create(P, { name: 'singular', type: 'string' }),
    metaDb.create(P, { name: 'type', type: 'string', enum: 'return ["collection","singleton","personal"]' }),
    metaDb.create(P, { name: 'path', type: 'string' }),
    metaDb.create(P, { name: 'reference', type: 'reference', reference: modelModel.id, description: 'For personal type: the collection model used for DB operations' }),
    metaDb.create(P, { name: 'icon', type: 'icon' }),
    metaDb.create(P, { name: 'label', type: 'code', description: 'Code string for dynamic label' }),
    metaDb.create(P, { name: 'hidden', type: 'boolean' }),
    metaDb.create(P, { name: 'view', type: 'string', enum: 'return ["table","card","map"]' }),
  ]);

  // User properties
  const [userName, userEmail, userNamemail, userLocation, userPic, userCv, userCvFilename, userBio, userTheme, userActive, userLastLogin] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string' }),
    metaDb.create(P, { name: 'email', type: 'string' }),
    metaDb.create(P, { name: 'namemail', type: 'string', computed: 'return `${this.name} <${this.email}>`' }),
    metaDb.create(P, { name: 'location', type: 'geopoint' }),
    metaDb.create(P, { name: 'pic', type: 'image' }),
    metaDb.create(P, { name: 'cv', type: 'pdf', description: 'Resume/CV document' }),
    metaDb.create(P, { name: 'cv-filename', type: 'string', description: 'Resume/CV file name', computed: 'var n = ""; if (this.cv instanceof File) n = this.cv.name; else if (typeof this.cv === "string" && this.cv) n = decodeURIComponent(this.cv.split("/").pop()); return n.replace(/\\.pdf$/i, "")' }),
    metaDb.create(P, { name: 'bio', type: 'string', multiline: 3 }),
    metaDb.create(P, { name: 'theme', type: 'string', enum: 'return ["light","dark","system"]' }),
    metaDb.create(P, { name: 'active', type: 'boolean', default: 'return true' }),
    metaDb.create(P, { name: 'lastLogin', type: 'date' }),
  ]);

  // Group properties
  const [groupName, groupUsers] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string' }),
    metaDb.create(P, { name: 'users', type: 'reference', reference: userModel.id, array: true }),
  ]);

  // Config properties
  const [cfgAnthropicApiKey, cfgAnthropicModel, cfgApiKey, cfgAuthDomain, cfgProjectId, cfgStorageBucket, cfgMessagingSenderId, cfgAppId, cfgMeasurementId] = await Promise.all([
    metaDb.create(P, { name: 'anthropicApiKey', type: 'password' }),
    metaDb.create(P, { name: 'anthropicModel', type: 'string' }),
    metaDb.create(P, { name: 'apiKey', type: 'string' }),
    metaDb.create(P, { name: 'authDomain', type: 'string' }),
    metaDb.create(P, { name: 'projectId', type: 'string' }),
    metaDb.create(P, { name: 'storageBucket', type: 'string' }),
    metaDb.create(P, { name: 'messagingSenderId', type: 'string' }),
    metaDb.create(P, { name: 'appId', type: 'string' }),
    metaDb.create(P, { name: 'measurementId', type: 'string' }),
  ]);

  // Project properties
  const [projName, projUrl, projColor, projLogo, projDesign, projTier, projDatabase, projParent, projConfig] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string' }),
    metaDb.create(P, { name: 'url', type: 'string' }),
    metaDb.create(P, { name: 'color', type: 'color' }),
    metaDb.create(P, { name: 'logo', type: 'image' }),
    metaDb.create(P, { name: 'design', type: 'string', enum: 'return ["native","mui","shadcn"]' }),
    metaDb.create(P, { name: 'tier', type: 'string', enum: 'return ["free","startup","enterprise"]' }),
    metaDb.create(P, { name: 'database', type: 'string', enum: 'return ["local","firebase"]' }),
    metaDb.create(P, { name: 'parent', type: 'string' }),
    metaDb.create(P, { name: 'config', type: 'object', reference: configModel.id, properties: [cfgAnthropicApiKey.id, cfgAnthropicModel.id, cfgApiKey.id, cfgAuthDomain.id, cfgProjectId.id, cfgStorageBucket.id, cfgMessagingSenderId.id, cfgAppId.id, cfgMeasurementId.id] }),
  ]);

  // Capability properties
  const [capAction, capModel] = await Promise.all([
    metaDb.create(P, { name: 'action', type: 'string', enum: 'return ["read","write","all"]' }),
    metaDb.create(P, { name: 'model', type: 'reference', reference: modelModel.id }),
  ]);

  // Authorization properties
  const [authGroup, authCapability, authProject, authCondition] = await Promise.all([
    metaDb.create(P, { name: 'group', type: 'reference', reference: groupModel.id }),
    metaDb.create(P, { name: 'capability', type: 'reference', reference: capabilityModel.id }),
    metaDb.create(P, { name: 'project', type: 'reference', reference: projectModel.id }),
    metaDb.create(P, { name: 'condition', type: 'code', description: 'Row-level condition: returns filter object applied when loading entities' }),
  ]);

  // Home properties
  const [homeWelcome, homeChart] = await Promise.all([
    metaDb.create(P, { name: 'welcome', type: 'custom', code: [
      'function({ React, app, navigate, components }) {',
      '  var h = React.createElement',
      '  var noop = function() {}',
      '  var models = React.useMemo(function() {',
      '    return app.authn',
      '      .filter(function(a) { return a.project && a.project.id === (app.project && app.project.id) })',
      '      .map(function(a) { return a.capability.model })',
      '      .filter(function(m) { return !m.hidden })',
      '  }, [app.authn, app.project])',
      '  var properties = [',
      '    { name: "icon", type: "icon" },',
      '    { name: "name", type: "string" },',
      '  ]',
      '  var rows = models.map(function(m) {',
      '    return [',
      '      { name: "id", state: [m.path, noop] },',
      '      { name: "icon", state: [m.icon || "", noop] },',
      '      { name: "name", state: [m.name, noop], label: m.name },',
      '    ]',
      '  })',
      '  return h(components.list, {',
      '    name: "",',
      '    properties: properties,',
      '    state: [rows, noop],',
      '    onRowClick: function(i) { navigate(models[i].path) },',
      '    view: "card",',
      '  })',
      '}',
    ].join('\n') }),
    metaDb.create(P, { name: 'chart', type: 'custom', code: [
      'function({ React, app }) {',
      '  var h = React.createElement',
      '  var _s = React.useState([])',
      '  var counts = _s[0], setCounts = _s[1]',
      '  React.useEffect(function() {',
      '    var models = app.authn',
      '      .filter(function(a) { return a.project && a.project.id === (app.project && app.project.id) })',
      '      .map(function(a) { return a.capability.model })',
      '      .filter(function(m) { return !m.hidden && m.type === "collection" })',
      '    Promise.all(models.map(function(m) {',
      '      return app.database.list(m).then(function(entities) {',
      '        return { name: m.name, count: entities.length }',
      '      })',
      '    })).then(setCounts)',
      '  }, [])',
      '  if (!counts.length) return null',
      '  var max = Math.max.apply(null, counts.map(function(c) { return c.count })) || 1',
      '  return h("div", { style: { padding: "1rem 0", maxWidth: "33%" } },',
      '    h("h4", { style: { margin: "0 0 0.75rem 0", fontWeight: 500 } }, "Entities"),',
      '    counts.map(function(item) {',
      '      return h("div", { key: item.name, style: { display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" } },',
      '        h("span", { style: { width: 100, textAlign: "right", fontSize: 13, opacity: 0.7 } }, item.name),',
      '        h("div", { style: { flex: 1, background: "#e5e7eb", borderRadius: 4, height: 24 } },',
      '          h("div", { style: { width: Math.max(item.count / max * 100, item.count > 0 ? 8 : 0) + "%", background: "#000", borderRadius: 4, height: 24, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8, color: "white", fontSize: 12, transition: "width 0.3s" } }, item.count)',
      '        )',
      '      )',
      '    })',
      '  )',
      '}',
    ].join('\n') }),
  ]);

  // Message properties
  const [msgTitle, msgBody, msgRead] = await Promise.all([
    metaDb.create(P, { name: 'title', type: 'string' }),
    metaDb.create(P, { name: 'body', type: 'string', multiline: 3 }),
    metaDb.create(P, { name: 'read', type: 'boolean' }),
  ]);

  // Notification properties
  const [notifUser, notifUserHidden, notifMessages] = await Promise.all([
    metaDb.create(P, { name: 'user', type: 'reference', reference: userModel.id }),
    metaDb.create(P, { name: 'user', type: 'reference', reference: userModel.id, hidden: 'return true' }),
    metaDb.create(P, { name: 'messages', type: 'reference', reference: messageModel.id, array: true }),
  ]);

  // Action properties
  const [actionName, actionCode, actionDescription, actionHidden, actionDisabled] = await Promise.all([
    metaDb.create(P, { name: 'name', type: 'string', required: true }),
    metaDb.create(P, { name: 'code', type: 'code' }),
    metaDb.create(P, { name: 'description', type: 'string', multiline: 3 }),
    metaDb.create(P, { name: 'hidden', type: 'code' }),
    metaDb.create(P, { name: 'disabled', type: 'code' }),
  ]);

  // Model actions reference property
  const modelActions = await metaDb.create(P, { name: 'actions', type: 'reference', array: true, reference: actionsModel.id });

  // ── Phase 2b: Create action entities ──

  const A = actionsModel as unknown as Model;
  const createCrud = async () => {
    const [save, del] = await Promise.all([
      metaDb.create(A, { name: 'Save', code: 'app.database.update(page.model, page.id, page.data).then(() => app.navigate(page.model.path))' }),
      metaDb.create(A, { name: 'Delete', code: 'await app.database.delete(page.model, page.id); app.navigate(page.model.path)', hidden: 'return !this.id' }),
    ]);
    return [save.id, del.id];
  };

  // Custom Save for Models — also creates 'all' capability + admin authorization
  const modelSave = await metaDb.create(A, {
    name: 'Save',
    code: [
      'await app.database.create(page.model, page.data, page.id)',
      '// Auto-authorize: create "all" capability and link groups to their projects',
      'var caps = await app.database.list("/capabilities", [], { model: page.id, action: "all" })',
      'var capId = caps.length ? caps[0].id : (await app.database.create("/capabilities", { action: "all", model: page.id })).id',
      'var pairs = [["Brampton End Users", "BRAMPTON"], ["Vancouver End Users", "Vancouver"]]',
      'for (var i = 0; i < pairs.length; i++) {',
      '  var groups = await app.database.list("/groups", [], { name: pairs[i][0] })',
      '  var projects = await app.database.list("/projects", [], { name: pairs[i][1] })',
      '  if (groups.length && projects.length) {',
      '    var auths = await app.database.list("/authorizations", [], { capability: capId, group: groups[0].id, project: projects[0].id })',
      '    if (!auths.length) await app.database.create("/authorizations", { capability: capId, group: groups[0].id, project: projects[0].id })',
      '  }',
      '}',
      'app.navigate(page.model.path)',
    ].join('\n'),
  });
  const modelDelete = await metaDb.create(A, {
    name: 'Delete',
    code: 'await app.database.delete(page.model, page.id); app.navigate(page.model.path)',
    hidden: 'return !this.id',
  });
  const modelCrud = [modelSave.id, modelDelete.id];

  // CRUD actions for models that need them
  const [propertyCrud, userCrud, groupCrud, projectCrud, capabilityCrud, authorizationCrud, messageCrud, notificationsCrud] = await Promise.all([
    createCrud(), // propertyModel
    createCrud(), // userModel
    createCrud(), // groupModel
    createCrud(), // projectModel
    createCrud(), // capabilityModel
    createCrud(), // authorizationModel
    createCrud(), // messageModel
    createCrud(), // notificationsModel
  ]);

  // Config actions — save navigates to parent entity
  // TODO: this is more complicated than it should be, simplify action navigation
  const [configSave, configDelete] = await Promise.all([
    metaDb.create(A, { name: 'Save', code: 'var parent = page.path.slice(0, -1); var p = parent.map(function(s) { return s.model.path + "/" + s.id }).join(""); app.database.update(page.model, page.id, page.data).then(function() { app.navigate(p || "/") })' }),
    metaDb.create(A, { name: 'Delete', code: 'var parent = page.path.slice(0, -1); var p = parent.map(function(s) { return s.model.path + "/" + s.id }).join(""); await app.database.delete(page.model, page.id); app.navigate(p || "/")', hidden: 'return !this.id' }),
  ]);
  const configCrud = [configSave.id, configDelete.id];

  // Save-only actions (no navigate)
  const [myProfileSave, myNotificationsSave] = await Promise.all([
    metaDb.create(A, { name: 'Save', code: 'app.database.update(page.model, page.id, page.data)' }),
    metaDb.create(A, { name: 'Save', code: 'app.database.update(page.model, page.id, page.data)' }),
  ]);

  // ── Phase 3: Update models with property IDs and action IDs ──

  await Promise.all([
    metaDb.update(M, propertyModel.id, { properties: propEditFieldIds, actions: propertyCrud }),
    metaDb.update(M, actionsModel.id, { properties: [actionName.id, actionCode.id, actionDescription.id, actionHidden.id, actionDisabled.id] }),
    metaDb.update(M, homeModel.id, { properties: [homeWelcome.id, homeChart.id] }),
    metaDb.update(M, modelModel.id, { properties: [modelName.id, modelSingular.id, modelType.id, modelPath.id, modelReference.id, modelIcon.id, modelLabel.id, modelHidden.id, modelView.id, propProperties.id, modelActions.id], actions: modelCrud }),
    metaDb.update(M, userModel.id, { properties: [userName.id, userEmail.id, userNamemail.id, userLocation.id, userPic.id, userCv.id, userCvFilename.id, userBio.id, userTheme.id, userActive.id, userLastLogin.id], actions: userCrud }),
    metaDb.update(M, groupModel.id, { properties: [groupName.id, groupUsers.id], actions: groupCrud }),
    metaDb.update(M, projectModel.id, { properties: [projName.id, projUrl.id, projColor.id, projLogo.id, projDesign.id, projTier.id, projDatabase.id, projParent.id, projConfig.id], actions: projectCrud }),
    metaDb.update(M, configModel.id, { properties: [cfgAnthropicApiKey.id, cfgAnthropicModel.id, cfgApiKey.id, cfgAuthDomain.id, cfgProjectId.id, cfgStorageBucket.id, cfgMessagingSenderId.id, cfgAppId.id, cfgMeasurementId.id], actions: configCrud }),
    metaDb.update(M, capabilityModel.id, { properties: [capAction.id, capModel.id], actions: capabilityCrud }),
    metaDb.update(M, authorizationModel.id, { properties: [authGroup.id, authCapability.id, authProject.id, authCondition.id], actions: authorizationCrud }),
    metaDb.update(M, myProfileModel.id, { properties: [userName.id, userEmail.id, userNamemail.id, userLocation.id, userPic.id, userCv.id, userCvFilename.id, userBio.id, userTheme.id], actions: [myProfileSave.id] }),
    metaDb.update(M, messageModel.id, { properties: [msgTitle.id, msgBody.id, msgRead.id], actions: messageCrud }),
    metaDb.update(M, notificationsModel.id, { properties: [notifUser.id, notifMessages.id], actions: notificationsCrud }),
    metaDb.update(M, myNotificationsModel.id, { properties: [notifUserHidden.id, notifMessages.id], actions: [myNotificationsSave.id] }),
  ]);

  // ── Phase 4: Entity data ──

  // Users
  const [sammy, sam, ester, samuca] = await Promise.all([
    entityDb.create(userModel as unknown as Model, { name: 'Sammy', email: 'sam@mksys.sh', location: '40.7128,-74.0060', pic: 'https://randomuser.me/api/portraits/women/1.jpg', cv: 'https://pdfobject.com/pdf/sample.pdf', active: true }),
    entityDb.create(userModel as unknown as Model, { name: 'Sam', email: 'sam@example.com', location: '37.7749,-122.4194', pic: 'https://randomuser.me/api/portraits/men/1.jpg', active: true }),
    entityDb.create(userModel as unknown as Model, { name: 'Ester', email: 'ester@example.com', location: '40.7128,-74.0060', pic: 'https://randomuser.me/api/portraits/women/2.jpg', cv: 'https://pdfobject.com/pdf/sample.pdf', active: true }),
    entityDb.create(userModel as unknown as Model, { name: 'Samuca', email: 'samuca@example.com', location: '37.7749,-122.4194', pic: 'https://randomuser.me/api/portraits/men/2.jpg', cv: 'https://pdfobject.com/pdf/sample.pdf', active: true }),
  ]);

  // Groups
  const [admins, editors] = await Promise.all([
    metaDb.create(groupModel as unknown as Model, { name: 'Admins', users: [sammy.id, sam.id] }),
    metaDb.create(groupModel as unknown as Model, { name: 'Editors', users: [sam.id, ester.id] }),
  ]);

  // Configs
  const mksysConfig = await metaDb.create(configModel as unknown as Model, {
    anthropicApiKey: import.meta.env.VITE_ANTHROPIC_API_KEY || '', anthropicModel: import.meta.env.VITE_ANTHROPIC_MODEL || '',
    apiKey: 'AIzaSyBeGpDhAab3Glb3KmtwemXNaToQK_3DBF8', authDomain: 'mksys-87fd8.firebaseapp.com', projectId: 'mksys-87fd8',
    storageBucket: 'mksys-87fd8.firebasestorage.app', messagingSenderId: '313237771208',
    appId: '1:313237771208:web:3734f9dabf31a4a9ebe6fd', measurementId: 'G-MPYJLQMKHZ',
  });

  // Projects
  metaDb.create(projectModel as unknown as Model, { ...rootProject, config: mksysConfig.id });
  const orderingProject = await metaDb.create(projectModel as unknown as Model, { name: 'ordering', url: 'ordering', color: '#1565c0', logo: '', design: 'shadcn', tier: 'startup', database: 'firebase', parent: rootProject.id, config: mksysConfig.id });

  // Capabilities — one per model, all with 'all' action
  const caps = new Map<string, string>();
  for (const model of allModels) {
    const cap = await metaDb.create(capabilityModel as unknown as Model, { action: 'all', model: model.id });
    caps.set(model.id, cap.id);
  }

  // Authorizations — admins get all capabilities on root + ordering projects
  for (const model of allModels) {
    metaDb.create(authorizationModel as unknown as Model, { group: admins.id, capability: caps.get(model.id), project: rootProject.id });
    metaDb.create(authorizationModel as unknown as Model, { group: admins.id, capability: caps.get(model.id), project: orderingProject.id });
  }
  // editors: my profile + my notifications on root project
  metaDb.create(authorizationModel as unknown as Model, { group: editors.id, capability: caps.get(myProfileModel.id), project: rootProject.id });
  metaDb.create(authorizationModel as unknown as Model, { group: editors.id, capability: caps.get(myNotificationsModel.id), project: rootProject.id });

  return {
    users: [sammy, sam, ester, samuca],
    models: { propertyModel, actionsModel, userModel, groupModel, projectModel, capabilityModel, authorizationModel, configModel },
    orderingProjectId: orderingProject.id,
  };
}
